/**
 * Pricing.tsx — V4-6-2 / V4-6-4.
 *
 * /pricing public page. Shows all plan tiers from copy.ts pricingRows.
 * /buying/pricing shows only the buying-lane rows.
 *
 * Public, no auth.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, PageHeader, marketingCopy } from '@aigovops/design-system';

const { pricingRows, pricingHeading, pricingLede } = marketingCopy;

export function Pricing() {
  const location = useLocation();
  const navigate = useNavigate();
  const isBuyingOnly = location.pathname.startsWith('/buying');

  const rows = isBuyingOnly
    ? pricingRows.filter((r) => r.lane === 'buying' || r.lane === null)
    : [...pricingRows];

  return (
    <main style={{
      maxWidth: 960,
      margin: '0 auto',
      padding: 'var(--space-6) var(--space-4)',
      fontFamily: 'var(--font-ui)',
    }}>
      <PageHeader
        title={pricingHeading}
        subtitle={isBuyingOnly
          ? 'AI procurement oversight tiers. Respond is free for your suppliers.'
          : pricingLede}
      />

      <div style={{
        display: 'grid',
        gap: 'var(--space-3)',
        marginTop: 'var(--space-4)',
      }}>
        {rows.map((row) => {
          const laneColor = row.lane === 'buying'
            ? 'var(--buying)'
            : row.lane === 'building'
              ? 'var(--building)'
              : 'var(--border-strong)';

          return (
            <Card key={row.plan_id}>
              <div style={{
                padding: 'var(--space-4)',
                borderLeft: `4px solid ${laneColor}`,
                display: 'grid',
                gap: 'var(--space-2)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}>
                    {row.name}
                    {row.popular && (
                      <span style={{
                        marginLeft: 'var(--space-2)',
                        fontSize: 'var(--font-size-3xs)',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-chip)',
                        background: 'var(--accent)',
                        color: 'var(--text-inverse)',
                        fontWeight: 600,
                      }}>
                        Popular
                      </span>
                    )}
                  </h3>
                  <span style={{
                    fontSize: 'var(--font-size-lg, 1.25rem)',
                    fontWeight: 700,
                    color: 'var(--text)',
                    fontFamily: 'var(--font-mono)',
                  }}>
                    {row.amount}
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: 'var(--font-size-2xs)',
                  color: 'var(--text-muted)',
                }}>
                  {row.description}
                </p>
                <p style={{
                  margin: 0,
                  fontSize: 'var(--font-size-3xs)',
                  color: 'var(--text-dim)',
                }}>
                  {row.subtitle}
                </p>
              </div>
            </Card>
          );
        })}
      </div>

      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        justifyContent: 'center',
        marginTop: 'var(--space-5)',
        flexWrap: 'wrap',
      }}>
        <Button onClick={() => navigate(isBuyingOnly ? '/buying' : '/signup')}>
          {isBuyingOnly ? 'Back to overview' : 'Get started'}
        </Button>
        {isBuyingOnly && (
          <Link
            to="/buying"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            }}
          >
            ← Buying overview
          </Link>
        )}
      </div>
    </main>
  );
}
